import { useEffect, useState } from 'react'
import { claimSharedDevice } from '../../api/client'
import { fetchOrganizationPositions } from '../../api/ws'
import { useAppStore } from '../../store/app'

// Claim a shared device — a voice satellite or a display screen — by the 6-digit
// code it's showing. The same /pair/claim path serves both, so this one form backs
// both the Voice and Displays segments of the Devices page (and the scan-to-claim
// QR, which pre-fills `initialCode`). It names the target household so a member
// pairing while viewing the wrong org notices before binding the device to it.
export function PairForm({
  orgID,
  orgName,
  showTemp,
  deviceKind,
  initialCode,
  onError,
  onPaired,
}: {
  orgID: string | null
  orgName?: string
  // Show the "Temporary display" checkbox (displays only — voice has no such thing).
  showTemp?: boolean
  // Which kind of device this form claims — drives the personal-mode caveat copy.
  // 'display' for the Displays segment, 'voice' for the Voice segment.
  deviceKind?: 'display' | 'voice'
  // Pre-fill the code (from a scanned QR). Updates if it changes.
  initialCode?: string
  onError: (e: string) => void
  // reclaimed: the claim re-paired an existing screen (which keeps its settings)
  // rather than adding a new one.
  onPaired: (name: string, reclaimed: boolean) => void
}) {
  const [code, setCode] = useState(initialCode ?? '')
  const [name, setName] = useState('')
  const [isTemp, setIsTemp] = useState(false)
  const [personal, setPersonal] = useState(false)
  const [routePosition, setRoutePosition] = useState('')
  // When set, RE-PAIR this browser into the chosen existing screen (keeping its
  // rules) instead of making a new one. Displays only.
  const [reclaimTarget, setReclaimTarget] = useState('')
  const [busy, setBusy] = useState(false)
  const codeOk = /^\d{6}$/.test(code)

  const positions = useAppStore((s) => s.organizationPositions)
  const agentInstances = useAppStore((s) => s.agentInstances)
  const ioDevices = useAppStore((s) => s.ioDevices)
  const displayScreens = useAppStore((s) => s.displayScreens)

  // Existing screens this claimer could re-pair into (displays only): their own
  // personal screens plus the household's shared ones. Picking one keeps all its
  // rules — the recovery path after a screen's browser lost its stored credential.
  const reclaimTargets =
    deviceKind === 'display'
      ? [
          ...ioDevices
            .filter((d) => d.form_factor === 'display' && !d.multi_human_user)
            .map((d) => ({ id: d.id, label: `${d.name} (personal)` })),
          ...displayScreens
            .filter((d) => !d.is_temp)
            .map((d) => ({ id: d.id, label: d.name })),
        ]
      : []

  // A personal voice puck can't be routed from the shared-devices list (that's
  // shared-only), and it has no other app path to an agent — so its route is chosen
  // HERE, at claim (passed as route_position_id). Displays don't route.
  const showRoutePicker = deviceKind === 'voice' && personal

  useEffect(() => {
    if (initialCode) setCode(initialCode)
  }, [initialCode])

  useEffect(() => {
    if (deviceKind === 'voice' && orgID) fetchOrganizationPositions(orgID)
  }, [deviceKind, orgID])

  // Route targets: durable (non-temp, non-eliminated) positions, labelled with the
  // live agent's name when there is one — "Thomas (Footman)" — matching the picker in
  // the Voice devices list.
  const agentByPosition = new Map<string, string>()
  for (const inst of agentInstances) {
    if (inst.retired_at || !inst.organization_position_id || !inst.name) continue
    const existing = agentByPosition.get(inst.organization_position_id)
    if (!existing || inst.status === 'active') {
      agentByPosition.set(inst.organization_position_id, inst.name)
    }
  }
  const routeOptions = positions
    .filter((p) => !p.eliminated_at && !p.is_temp)
    .map((p) => {
      const agent = agentByPosition.get(p.id)
      return { id: p.id, label: agent ? `${agent} (${p.name})` : p.name }
    })

  async function submit() {
    if (!codeOk || busy) return
    setBusy(true)
    try {
      const res = await claimSharedDevice({
        code,
        organizationID: orgID ?? undefined,
        name: name.trim() || undefined,
        // Re-pairing into an existing screen keeps that screen's shape/name — the
        // new-screen options don't apply.
        isTemp: reclaimTarget ? undefined : showTemp ? isTemp : undefined,
        personal: reclaimTarget ? undefined : personal || undefined,
        routePositionID: showRoutePicker && routePosition ? routePosition : undefined,
        reclaimScreenID: reclaimTarget || undefined,
      })
      setCode('')
      setName('')
      setIsTemp(false)
      setPersonal(false)
      setRoutePosition('')
      setReclaimTarget('')
      onPaired(res.name || name.trim() || 'device', !!res.reclaimed)
    } catch (e) {
      onError((e as Error)?.message ?? String(e))
    } finally {
      setBusy(false)
    }
  }

  // The personal-mode caveat differs by device: a screen's personal-ness is WRITE
  // authority (not confidentiality — the room can still see it); a puck's is speaking
  // AS you (possession-based, so keep it somewhere private).
  const personalCaveat =
    deviceKind === 'voice'
      ? 'Personal: this speaker treats whatever it hears as coming from you, and only you. There’s no voice check — keep it somewhere private.'
      : 'Personal: only you can put content on this screen. (Anyone nearby can still see what’s on it — this controls who can publish, not who can look.)'

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {deviceKind === 'voice' ? 'Connect Home Assistant' : 'Pair a device'}
      </div>
      {orgName && (
        <p style={{ margin: '0 0 6px', fontSize: 13 }}>
          Pairing to <strong>{orgName}</strong>. Switch household first if that's not right.
        </p>
      )}
      <p style={{ margin: '0 0 10px', opacity: 0.7, fontSize: 13 }}>
        {deviceKind === 'voice' ? (
          <>
            Enter the 6-digit code the <strong>Hearth Cmd</strong> integration shows in
            Home Assistant. This links your whole Home Assistant as a <strong>bridge</strong>
            — its voice satellites then appear beneath it here, each routable to its own
            agent. (Name and rename it in Home Assistant.)
          </>
        ) : (
          <>
            Enter the 6-digit code the device is showing — a voice satellite during its
            setup, or a display screen running <code>hearth display</code>.
          </>
        )}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="6-digit code"
          inputMode="numeric"
          autoComplete="one-time-code"
          style={{ fontSize: 16, letterSpacing: 2 }}
        />
        {reclaimTargets.length > 0 && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
            <span style={{ opacity: 0.8 }}>Re-pairing a screen you already set up?</span>
            <select
              value={reclaimTarget}
              onChange={(e) => setReclaimTarget(e.target.value)}
              style={{ fontSize: 16 }}
            >
              <option value="">Set up as a new screen</option>
              {reclaimTargets.map((t) => (
                <option key={t.id} value={t.id}>
                  Re-pair: {t.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {reclaimTarget ? (
          <p
            style={{
              margin: 0,
              fontSize: 12,
              lineHeight: 1.4,
              opacity: 0.85,
              padding: '6px 8px',
              background: '#eef6ff',
              borderRadius: 4,
            }}
          >
            This screen keeps its name and all the rules you set up for it — you're just
            reconnecting its browser.
          </p>
        ) : (
          <>
            {/* A voice bridge is named in Home Assistant (and the relay defaults it
                to "<Household> - Home Assistant"), so don't ask again here — leaving
                the claim name blank keeps the HA-provided name. */}
            {deviceKind !== 'voice' && (
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name (optional, e.g. Kitchen screen)"
                autoCapitalize="words"
                style={{ fontSize: 16 }}
              />
            )}
            {showTemp && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={isTemp}
                  onChange={(e) => setIsTemp(e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                Temporary display — removes itself once its browser is gone
              </label>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
              <input
                type="checkbox"
                checked={personal}
                onChange={(e) => setPersonal(e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
              Personal — bound to me, not shared with the household
            </label>
            {personal && (
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  lineHeight: 1.4,
                  opacity: 0.8,
                  padding: '6px 8px',
                  background: '#fff8e6',
                  borderRadius: 4,
                }}
              >
                {personalCaveat}
              </p>
            )}
          </>
        )}
        {showRoutePicker && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
            <span style={{ opacity: 0.8 }}>Which agent should this speaker talk to?</span>
            <select
              value={routePosition}
              onChange={(e) => setRoutePosition(e.target.value)}
              style={{ fontSize: 16 }}
            >
              <option value="">Choose later</option>
              {routeOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <button onClick={submit} disabled={!codeOk || busy}>
          {busy ? 'Pairing…' : 'Pair'}
        </button>
      </div>
    </div>
  )
}
